/*
  Herbies cart: THE add-to-cart for product pages.
  ================================================
  One click = the paid line through /cart/add.js (the one AddToCart Shopify
  reports for it), then the free bundle units and tier gifts added to the same
  cart by the bundles app (/api/cart-extras, server-side, so the browser-side
  tracking does not count them as extra add-to-carts), then straight to
  checkout (or the drawer for templates set to "add to cart"). If the app call
  fails, the browser adds the missing extras itself. Nothing patches
  window.fetch and nothing adds lines later, so there is no race with the
  redirect.

  Line contract with the discount Function (bundle-bxgy in the bundles app),
  do not rename:
    _bundle_free / _bundle_min_paid  free units of a BOGO tier, a one-time line
    _bundle_gift                     a tier gift (made free by the Function)
    _bundle_host / _bundle_tier      a bundle card that adds its own product
                                     (Detox Bundle), counted as the top tier
    _bundle_variant / _bundle_exp    A or B of the running A/B-test on that
                                     product, and its id (see "A/B-test" below)

  Gift config comes from the bundles app (/api/bundle-config), fetched when the
  page loads and cached for this session; a click waits for it (at most a few
  seconds). If it cannot be loaded the order still goes through without the
  gifts. The Function prices everything and never trusts the storefront.

  Removing gifts that no longer qualify (customer lowers the quantity in the
  drawer) is done by the drawer's cleanup in sections/cart-drawer.liquid, using
  HerbiesCart.eligibleGiftIds below. That cleanup only ever removes lines.
*/
(function () {
  'use strict';

  var cfgEl = document.getElementById('herbies-cart-config');
  var SETTINGS = {};
  try { SETTINGS = JSON.parse((cfgEl && cfgEl.textContent) || '{}'); } catch (_e) { SETTINGS = {}; }

  var CACHE_KEY = 'herbies-bundle-config-v1';
  var CACHE_MS = 10 * 60 * 1000;
  var CONFIG_WAIT_MS = 3500;
  var EXTRAS_WAIT_MS = 9000; // the app gives up on Shopify after 6 s

  // ---------------------------------------------------------------------------
  // Bundle config (gifts per tier), loaded once per page and cached per session.
  // ---------------------------------------------------------------------------
  var configPromise = null;

  function readCache() {
    // A test link (?hb_ab=A or B) always reads the latest settings: whoever
    // just started a test should see it, not this tab's copy from before.
    if (forcedVariant()) return null;
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var c = JSON.parse(raw);
      if (!c || !c.at || Date.now() - c.at > CACHE_MS || !c.config) return null;
      return c.config;
    } catch (_e) { return null; }
  }

  function writeCache(config) {
    try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), config: config })); } catch (_e) { /* private mode */ }
  }

  function loadConfig() {
    if (configPromise) return configPromise;
    var cached = readCache();
    if (cached) {
      configPromise = Promise.resolve(cached);
      return configPromise;
    }
    if (!SETTINGS.apiBase || !SETTINGS.shop) {
      configPromise = Promise.resolve({ products: [] });
      return configPromise;
    }
    configPromise = fetch(
      SETTINGS.apiBase + '/api/bundle-config?shop=' + encodeURIComponent(SETTINGS.shop),
      forcedVariant() ? { cache: 'no-cache' } : undefined
    )
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        var config = j && Array.isArray(j.products) ? j : null;
        if (config) writeCache(config);
        return config || { products: [] };
      })
      .catch(function () {
        configPromise = null; // try again on the next click
        return { products: [] };
      });
    return configPromise;
  }

  /** The config, or an empty one when it takes longer than `ms`. */
  function configWithin(ms) {
    return Promise.race([
      loadConfig(),
      new Promise(function (resolve) { setTimeout(function () { resolve({ products: [] }); }, ms); }),
    ]);
  }

  function productConfig(config, productId) {
    var list = (config && config.products) || [];
    for (var i = 0; i < list.length; i++) {
      if (String(list[i].id) === String(productId)) return list[i];
    }
    return null;
  }

  function appliesMatch(applies, isSub) {
    if (!applies || applies === 'all') return true;
    if (applies === 'onetime') return !isSub;
    if (applies === 'subscription') return isSub;
    return true;
  }

  /** The heaviest tier of a product (the Detox Bundle always counts as that). */
  function topTier(productCfg) {
    var top = 0;
    ((productCfg && productCfg.tiers) || []).forEach(function (t) {
      var g = Number(t && t.group);
      if (g > top) top = g;
    });
    return top;
  }

  // ---------------------------------------------------------------------------
  // A/B-test on the gifts of one or both bundle tiers of a product (set up in
  // the bundles app).
  // A visitor is drawn into A or B once per experiment and keeps it (cookie
  // hb_ab, 60 days; ?hb_ab=A or ?hb_ab=B forces one, for testing). In B each
  // tested tier gives its B gifts, and the product page can show another tier
  // image, title, subtext and extra gift lines in that card, hide the gifts
  // box and the one-time purchase link, and switch the bundles between side by
  // side and stacked.
  // Every line of an add carries _bundle_variant + _bundle_exp: the Function
  // only makes B gifts free on lines that say B for the running experiment,
  // and the orders webhook counts purchases per variant.
  // ---------------------------------------------------------------------------
  var AB_DAYS = 60;
  var abDrawn = {}; // per experiment id, so one page never draws twice (also without cookies)

  function readAbCookie() {
    var out = {};
    var m = document.cookie.match(/(?:^|;\s*)hb_ab=([^;]*)/);
    if (!m) return out;
    decodeURIComponent(m[1]).split('~').forEach(function (pair) {
      var p = pair.split('.');
      if (p.length === 2 && /^[a-z0-9]{1,20}$/i.test(p[0]) && (p[1] === 'A' || p[1] === 'B')) out[p[0]] = p[1];
    });
    return out;
  }

  function writeAbCookie(map) {
    var parts = Object.keys(map).slice(-20).map(function (k) { return k + '.' + map[k]; });
    document.cookie = 'hb_ab=' + encodeURIComponent(parts.join('~')) +
      '; path=/; max-age=' + AB_DAYS * 24 * 3600 + '; SameSite=Lax';
  }

  function forcedVariant() {
    var m = /[?&]hb_ab=([AB])(?:&|$)/i.exec(window.location.search);
    return m ? m[1].toUpperCase() : null;
  }

  /**
   * The running experiment on a product (the app only sends running ones):
   * { exp: id + weight, groups: { tier group: its B }, page: B of the page }.
   * Every tested tier carries the same id; the page settings are on each.
   */
  function experimentOf(productCfg) {
    var tiers = (productCfg && productCfg.tiers) || [];
    var x = null;
    for (var i = 0; i < tiers.length; i++) {
      var e = tiers[i] && tiers[i].experiment;
      if (!e || !e.id) continue;
      if (!x) x = { exp: e, groups: {}, page: e.b || {} };
      if (String(e.id) === String(x.exp.id)) x.groups[String(Number(tiers[i].group))] = e.b || {};
    }
    return x;
  }

  /** { id, groups, page, variant } for this visitor, drawn once per experiment. */
  function abFor(productCfg) {
    var x = experimentOf(productCfg);
    if (!x) return null;
    var map = readAbCookie();
    var variant = forcedVariant() || abDrawn[x.exp.id] || map[x.exp.id];
    if (variant !== 'A' && variant !== 'B') {
      variant = Math.random() * 100 < Number(x.exp.weight || 0) ? 'B' : 'A';
    }
    abDrawn[x.exp.id] = variant;
    if (map[x.exp.id] !== variant) {
      delete map[x.exp.id]; // newest last, so the oldest drop off first
      map[x.exp.id] = variant;
      writeAbCookie(map);
    }
    return { id: String(x.exp.id), groups: x.groups, page: x.page, variant: variant, cfg: productCfg };
  }

  /**
   * Gift variant ids unlocked at `qty` units: every tier reached (qty >= buy +
   * free) adds its gifts, one unit per gift variant, filtered on whether the
   * bundle line is a subscription. In variant B the tested tier gives its B
   * gifts instead. Same rules as the Function.
   */
  function giftIdsFor(productCfg, qty, isSub, ab, paid) {
    var out = {};
    if (paid == null) paid = qty;
    ((productCfg && productCfg.tiers) || []).forEach(function (t) {
      if (!t) return;
      var bTier = ab && ab.variant === 'B' ? ab.groups[String(Number(t.group))] : null;
      // B with fewer free units: reached on paid units plus B's free units (as the Function).
      if (bTier && typeof bTier.free === 'number') {
        if (paid < Number(t.buy) || qty < Number(t.buy) + bTier.free) return;
      } else if (qty < Number(t.group)) {
        return;
      }
      var gifts = bTier ? (bTier.gifts || []) : (t.gifts || []);
      gifts.forEach(function (g) {
        if (g && g.id && appliesMatch(g.applies, isSub)) out[String(g.id)] = true;
      });
    });
    return Object.keys(out);
  }

  /**
   * Which gift variants a cart is entitled to, from its paid, free and host
   * lines. Used by the drawer cleanup to remove gifts that no longer qualify.
   * B gifts count only while the lines say B for the experiment that is still
   * running, the same rule as the Function; after a stop they are removed.
   */
  function eligibleGiftIds(cart, config) {
    var perProduct = {};
    (cart.items || []).forEach(function (i) {
      var p = i.properties || {};
      if (p._bundle_gift) return;
      var pid = String(i.product_id);
      var host = p._bundle_host ? String(p._bundle_host) : null;
      var key = host || pid;
      var e = perProduct[key] || (perProduct[key] = { qty: 0, paid: 0, host: false, sub: false, exp: null });
      if (host) e.host = true;
      else e.qty += i.quantity;
      if (!host && !p._bundle_free) e.paid += i.quantity;
      if (i.selling_plan_allocation) e.sub = true;
      if (p._bundle_variant === 'B' && p._bundle_exp) e.exp = String(p._bundle_exp);
    });
    var ids = {};
    Object.keys(perProduct).forEach(function (pid) {
      var cfg = productConfig(config, pid);
      if (!cfg) return;
      var e = perProduct[pid];
      var qty = e.host ? Math.max(e.qty, topTier(cfg)) : e.qty;
      var x = experimentOf(cfg);
      var ab = x && e.exp === String(x.exp.id) ? { variant: 'B', groups: x.groups } : null;
      giftIdsFor(cfg, qty, e.sub, ab, e.host ? qty : e.paid).forEach(function (id) { ids[id] = true; });
    });
    return ids;
  }

  // ---------------------------------------------------------------------------
  // The add.
  // ---------------------------------------------------------------------------
  var adding = false;

  // A spinner in the button while it works; the colour stays (changing it flashed the button white).
  var STYLE_ID = 'herbies-cart-style';
  function ensureStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var st = document.createElement('style');
    st.id = STYLE_ID;
    st.textContent =
      '.herbies-adding{cursor:progress!important;position:relative}' +
      '.herbies-adding .herbies-spinner{display:inline-block;width:1em;height:1em;margin-left:.5em;vertical-align:-0.15em;' +
      'border:2px solid currentColor;border-right-color:transparent;border-radius:50%;animation:herbies-spin .7s linear infinite}' +
      '@keyframes herbies-spin{to{transform:rotate(360deg)}}';
    document.head.appendChild(st);
  }

  function lockButton(btn) {
    if (!btn) return;
    ensureStyle();
    btn.dataset.herbiesAdding = '1';
    btn.dataset.styleSnapshot = btn.getAttribute('style') || '';
    btn.style.setProperty('pointer-events', 'none', 'important');
    btn.setAttribute('aria-busy', 'true');
    btn.classList.add('herbies-adding');
    if (!btn.querySelector('.herbies-spinner')) {
      var label = btn.querySelector('span') || btn; // next to the button text, not under it
      var sp = document.createElement('span');
      sp.className = 'herbies-spinner';
      sp.setAttribute('aria-hidden', 'true');
      label.appendChild(sp);
    }
  }

  function releaseButton(btn) {
    if (!btn) return;
    if (typeof btn.dataset.styleSnapshot === 'string') {
      if (btn.dataset.styleSnapshot === '') btn.removeAttribute('style');
      else btn.setAttribute('style', btn.dataset.styleSnapshot);
    }
    delete btn.dataset.styleSnapshot;
    delete btn.dataset.herbiesAdding;
    btn.removeAttribute('aria-busy');
    btn.classList.remove('herbies-adding');
    var sp = btn.querySelector('.herbies-spinner');
    if (sp) sp.remove();
  }

  function showError(form, message) {
    var box = (form && form.closest('product-info, .product__info-wrapper, section')) || document;
    var el = box.querySelector('.product-form__error-message');
    var wrap = box.querySelector('.product-form__error-message-wrapper');
    if (el) el.textContent = message;
    if (wrap) wrap.removeAttribute('hidden');
  }

  function wait(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }

  function tickedSubscription() {
    return document.querySelector('.subscribe-save-wrapper input[type="checkbox"]:checked, .subscribe-save-wrapper-dynamic input[type="checkbox"]:checked');
  }

  function planFor(form) {
    var input = form.querySelector('[name="selling_plan"]');
    if (input && input.value) return input.value;
    var ctx = window.subscriptionContext;
    return (ctx && ctx.sellingPlanId) || '';
  }

  /**
   * The form, once the bundle and subscription scripts have filled in the
   * variant and, when the subscription toggle is ticked, the selling plan.
   * A click right after load can beat those scripts by a few hundred ms.
   */
  function readyForm(form) {
    var tries = 0;
    return new Promise(function (resolve) {
      (function check() {
        var f = form || document.querySelector('form[action*="/cart/add"].product-buy-form-pdp') || document.querySelector('form[action*="/cart/add"]');
        var id = f && f.querySelector('[name="id"]');
        var planReady = !tickedSubscription() || (f && planFor(f));
        if (f && id && id.value && planReady) return resolve(f);
        if (++tries > 16) return resolve(f || null);
        setTimeout(check, 150);
      })();
    });
  }

  /** The main line(s) from the selected bundle card and the form. */
  function mainItems(form) {
    var scope = form.closest('product-info, .product__info-wrapper, section') || document;
    var card = scope.querySelector('.quantity-bundle-card[data-selected="true"]') || document.querySelector('.quantity-bundle-card[data-selected="true"]');
    var variantId = Number((form.querySelector('[name="id"]') || {}).value);
    // Only when the toggle is ticked: the context can hold a plan while one-time is chosen.
    var sellingPlan = (form.querySelector('[name="selling_plan"]') || {}).value || (tickedSubscription() ? planFor(form) : '');
    var productId = form.getAttribute('data-product-id') || SETTINGS.productId || '';
    var total = card ? parseInt(card.getAttribute('data-quantity'), 10) : NaN;
    var paid = card ? parseInt(card.getAttribute('data-bogo-multiplier'), 10) : NaN;
    var bundleVariant = card ? Number(card.getAttribute('data-bundle-variant') || 0) : 0;
    var isSub = !!sellingPlan;

    if (bundleVariant) {
      // A bundle card with its own product (Detox Bundle): that product once, counted as the top tier.
      var host = { id: bundleVariant, quantity: 1, properties: { _bundle_host: String(productId), _bundle_tier: 'bundle3' } };
      if (sellingPlan) host.selling_plan = Number(sellingPlan);
      return { items: [host], productId: productId, qty: null, paid: null, isSub: isSub };
    }
    if (variantId && card && paid > 0 && total > paid) {
      // A BOGO tier: paid units keep the subscription; the free units are a separate one-time line.
      var paidItem = { id: variantId, quantity: paid };
      if (sellingPlan) paidItem.selling_plan = Number(sellingPlan);
      var freeItem = { id: variantId, quantity: total - paid, properties: { _bundle_free: 'true', _bundle_min_paid: String(paid) } };
      return { items: [paidItem, freeItem], productId: productId, qty: total, paid: paid, isSub: isSub };
    }
    var qtyInput = form.querySelector('[name="quantity"]');
    var qty = Math.max(1, parseInt((qtyInput && qtyInput.value) || (card && card.getAttribute('data-quantity')) || '1', 10) || 1);
    var item = { id: variantId, quantity: qty };
    if (sellingPlan) item.selling_plan = Number(sellingPlan);
    return { items: [item], productId: productId, qty: qty, paid: qty, isSub: isSub };
  }

  function postAdd(items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items }),
    }).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (body) { return { ok: r.ok, status: r.status, body: body }; });
    });
  }

  /**
   * Free units and tier gifts, added to the same cart by the bundles app
   * (Storefront API, server-side). Shopify's browser tracking reports every
   * line the browser adds as an add-to-cart, so adding these here would turn
   * one click into 3-6 AddToCart events. If the app cannot do it in time the
   * browser adds them after all: the customer always gets the free items.
   */
  function addExtras(extras, productId) {
    if (!extras.length) return Promise.resolve();
    var viaApp = fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        return fetch(SETTINGS.apiBase + '/api/cart-extras', {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain' }, // a simple request: no CORS preflight
          body: JSON.stringify({
            shop: SETTINGS.shop,
            cartToken: cart.token,
            productId: Number(productId),
            lines: extras.map(function (l) {
              return {
                variantId: l.id,
                quantity: l.quantity,
                attributes: Object.keys(l.properties || {}).map(function (k) { return { key: k, value: String(l.properties[k]) }; }),
              };
            }),
          }),
        });
      })
      .then(function (r) { if (!r.ok) throw new Error('cart-extras ' + r.status); return r.json(); });

    var timeout = new Promise(function (_, reject) { setTimeout(function () { reject(new Error('cart-extras timeout')); }, EXTRAS_WAIT_MS); });

    return Promise.race([viaApp, timeout]).catch(function (err) {
      try { console.warn('[herbies-cart] extras via the browser:', err && err.message); } catch (_e) { /* noop */ }
      // Only what is not in the cart yet: a slow app call may have landed after all,
      // and a second free-unit line would be charged.
      return fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var missing = extras.filter(function (l) {
            var kind = l.properties && l.properties._bundle_gift ? '_bundle_gift' : '_bundle_free';
            return !(cart.items || []).some(function (i) { return i.variant_id === l.id && i.properties && i.properties[kind]; });
          });
          if (!missing.length) return;
          return postAdd(missing).then(function (r) {
            if (r.ok) return;
            // A sold-out gift must not cost the customer the free units.
            var freeOnly = missing.filter(function (l) { return !(l.properties && l.properties._bundle_gift); });
            if (freeOnly.length && freeOnly.length < missing.length) return postAdd(freeOnly);
          });
        })
        .catch(function () { /* the paid line is in; never block the checkout on extras */ });
    });
  }

  function goCheckout() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    window.location.href = root.replace(/\/+$/, '') + '/checkout';
  }

  function openDrawer(cartData) {
    var drawer = document.querySelector('cart-drawer');
    try {
      if (window.publish && window.PUB_SUB_EVENTS) window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'herbies-cart', cartData: cartData });
    } catch (_e) { /* noop */ }
    if (drawer && typeof drawer.refreshCartDrawer === 'function') drawer.refreshCartDrawer(null);
    else window.location.href = (window.routes && window.routes.cart_url) || '/cart';
  }

  /**
   * Add the selected bundle (with its gifts) in one request.
   * opts: { form, button, mode: 'checkout' | 'drawer' }
   */
  function add(opts) {
    opts = opts || {};
    var btn = opts.button || null;
    if (adding) return Promise.resolve(false);
    adding = true;
    lockButton(btn);

    return Promise.all([readyForm(opts.form), configWithin(CONFIG_WAIT_MS)])
      .then(function (res) {
        var form = res[0];
        var config = res[1];
        if (!form) throw new Error('no product form');
        var main = mainItems(form);
        if (!main.items.length || !main.items[0].id) throw new Error('no variant selected');

        var cfg = productConfig(config, main.productId);
        var ab = abFor(cfg);
        // B with fewer free units on this bundle: the same paid units, fewer free ones.
        var bTier = ab && ab.variant === 'B' && main.qty !== null ? ab.groups[String(main.qty)] : null;
        if (bTier && typeof bTier.free === 'number' && main.items.length === 2) {
          if (bTier.free > 0) main.items[1].quantity = bTier.free;
          else main.items.pop();
          main.qty = main.paid + bTier.free;
        }
        var qty = main.qty === null ? topTier(cfg) : main.qty;
        var gifts = giftIdsFor(cfg, qty, main.isSub, ab, main.paid === null ? qty : main.paid).map(function (id) {
          return { id: Number(id), quantity: 1, properties: { _bundle_gift: '1' } };
        });
        if (ab) {
          main.items.concat(gifts).forEach(function (l) {
            l.properties = l.properties || {};
            l.properties._bundle_variant = ab.variant;
            l.properties._bundle_exp = ab.id;
          });
        }

        // The paid line goes through /cart/add (the one AddToCart for this click);
        // free units and gifts are added server-side, see addExtras.
        var paid = [main.items[0]];
        var extras = main.items.slice(1).concat(gifts);

        var attempt = 0;
        function tryAdd() {
          attempt++;
          return postAdd(paid).then(function (r) {
            if (r.ok) return r.body;
            if (attempt < 3 && (r.status === 409 || r.status >= 500)) return wait(400).then(tryAdd);
            var err = new Error((r.body && (r.body.description || r.body.message)) || 'Could not add to cart');
            err.status = r.status;
            throw err;
          }, function (netErr) {
            if (attempt < 3) return wait(400).then(tryAdd);
            throw netErr;
          });
        }

        return tryAdd()
          .then(function (cartData) { return addExtras(extras, main.productId).then(function () { return cartData; }); })
          .then(function (cartData) {
          if (opts.mode === 'drawer') {
            adding = false;
            releaseButton(btn);
            openDrawer(cartData);
          } else {
            goCheckout(); // the button stays locked while we leave
          }
          return true;
        });
      })
      .catch(function (err) {
        adding = false;
        releaseButton(btn);
        showError(opts.form, (err && err.message) || 'Something went wrong, please try again.');
        try { console.warn('[herbies-cart]', err); } catch (_e) { /* noop */ }
        return false;
      });
  }

  // Back from checkout: the browser may restore this page from its cache with
  // the button still locked. Unlock it.
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    adding = false;
    document.querySelectorAll('[data-herbies-adding]').forEach(releaseButton);
  });

  // ---------------------------------------------------------------------------
  // A/B-test: what the visitor sees. Only B changes the page, and only on the
  // product page of the tested product. CSS for the classes below is in
  // snippets/herbies-cart.liquid.
  //   hb-ab-wait      config not known yet: bundles and gifts box hidden, at
  //                   most AB_GATE_MS (B arriving later is still applied)
  //   hb-ab-relayout  B switches the layout: bundles hidden until DOMContentLoaded,
  //                   because the template's layout <style> comes after them
  //   hb-ab-hidebox   B hides the gifts box
  //   hb-ab-noonetime B hides the "One-Time Purchase" link (subscription only)
  //   hb-ab-nosave    B hides the "Save X%" pills on the bundles
  //   hb-ab-ownbox    B fills the gifts box itself: its row stays hidden until then
  //   hb-ab-notrans   transitions off while the layout is swapped
  // abApply() is idempotent; it runs when the config is known, right after the
  // bundle block (snippets/quantity-bundle-block.liquid) and on DOMContentLoaded.
  // ---------------------------------------------------------------------------
  var AB_GATE_MS = 800;
  var docEl = document.documentElement;
  var pageAb; // undefined = not known yet, null = no test here
  var pageAbPromise = null;

  function pageAbReady() {
    if (pageAbPromise) return pageAbPromise;
    pageAbPromise = SETTINGS.productId
      ? loadConfig().then(function (config) {
          if (pageAb === undefined) pageAb = abFor(productConfig(config, SETTINGS.productId));
          return pageAb;
        })
      : Promise.resolve(null);
    return pageAbPromise;
  }

  /* Layouts of the existing templates, for the bundles' section (P = its #id).
     stack: product.oregano-listicle-* ; cols: product.oregano-3col-* */
  var LAYOUT_CSS = {
    stack: function (P) {
      return P + ' .quantity-bundle-container{flex-direction:column;gap:14px}' +
        P + ' .quantity-bundle-card{border-radius:4px!important;max-width:100%;width:100%;min-height:0;margin-bottom:0!important;flex:0 0 auto}' +
        P + ' .quantity-bundle-horizontal-inner{flex-direction:row;align-items:center;gap:12px;height:auto;overflow:visible}' +
        P + ' .quantity-bundle-horizontal-image{height:46px;width:46px}' +
        P + ' .quantity-bundle-horizontal-text{flex:1 1 auto;align-items:flex-start;text-align:left;gap:2px}' +
        P + ' .quantity-bundle-title{text-align:left;line-height:1.25;grid-area:titel;margin:0}' +
        P + ' .quantity-bundle-pricing{margin-left:auto;align-items:flex-end;text-align:right;white-space:nowrap;grid-area:prijs;align-self:center}' +
        P + ' .quantity-bundle-badge{left:auto!important;right:12px!important;top:-7px!important;bottom:auto!important;height:auto!important;box-shadow:0 0 0 2px #fff,0 1px 3px rgba(0,0,0,.12)!important;width:auto!important;min-width:0!important;transform:none!important;display:inline-block!important;padding:2px 9px!important;border-radius:4px!important;font-size:9.5px!important;letter-spacing:.4px;line-height:1.5!important}' +
        P + ' .quantity-bundle-selector{margin-bottom:14px!important}' +
        P + ' .quantity-bundle-save-pill{font-size:10.5px!important;font-weight:600!important;padding:1px 7px!important;border-radius:4px!important;line-height:1.35;grid-area:pil;justify-self:start;margin:0}' +
        P + ' .quantity-bundle-content{display:grid;grid-template-columns:1fr auto;grid-template-areas:"titel prijs" "pil prijs";align-items:center;column-gap:12px;row-gap:2px;width:100%;text-align:left}' +
        P + ' .quantity-bundle-content>.quantity-bundle-description,' + P + ' .quantity-bundle-content>.hb-ab-note{grid-column:1;text-align:left;margin:0}' +
        P + ' .quantity-price-row{flex-direction:row;align-items:baseline;gap:6px}' +
        '@media (max-width:749px){' +
          P + ' .quantity-bundle-card{padding:12px 14px!important}' +
          P + ' .quantity-bundle-horizontal-image{height:44px;width:78px}' +
          P + ' .quantity-bundle-content{padding-left:0}' +
          P + ' .quantity-bundle-horizontal-inner{gap:10px}' +
          P + ' .quantity-bundle-title{font-size:14px!important}' +
        '}' +
        '@media (min-width:750px){' +
          P + ' .quantity-bundle-horizontal-inner{gap:34px}' +
          P + ' .quantity-bundle-horizontal-image{height:68px;width:68px}' +
          P + ' .quantity-bundle-card{padding:18px 22px 18px 26px!important}' +
          P + ' .quantity-bundle-title{font-size:16.5px!important;line-height:1.3}' +
          P + ' .quantity-bundle-save-pill{font-size:12px!important;padding:2px 9px!important}' +
          P + ' .quantity-bundle-price,' + P + ' .quantity-price-row>*{font-size:18px!important}' +
          P + ' .quantity-bundle-badge{font-size:10.5px!important;padding:3px 11px!important}' +
        '}';
    },
    cols: function (P) {
      return P + ' .quantity-bundle-container{padding-top:16px}' +
        P + ' .quantity-bundle-card{max-width:none;flex:1 1 0;min-width:0;margin-bottom:0!important;border-radius:4px!important}' +
        P + ' .quantity-bundle-title,' + P + ' .quantity-bundle-content{text-align:center}' +
        P + ' .quantity-bundle-badge{left:50%!important;right:auto!important;transform:translateX(-50%)!important;top:-9px!important;bottom:auto!important;width:auto!important;min-width:0!important;height:auto!important;display:inline-block!important;white-space:nowrap!important;padding:2px 9px!important;border-radius:4px!important;font-size:9.5px!important;letter-spacing:.4px;line-height:1.5!important;box-shadow:0 0 0 2px #fff,0 1px 3px rgba(0,0,0,.12)!important}' +
        P + ' .quantity-bundle-save-pill{font-weight:600!important;border-radius:4px!important;line-height:1.35}' +
        P + ' .quantity-bundle-selector{margin-bottom:14px!important}' +
        '@media (min-width:750px){' +
          P + ' .quantity-bundle-card{padding:14px 10px!important}' +
          P + ' .quantity-bundle-title{font-size:14.5px!important;line-height:1.25}' +
          P + ' .quantity-bundle-save-pill{font-size:11px!important;padding:2px 8px!important}' +
        '}' +
        '@media (max-width:749px){' +
          P + ' .quantity-bundle-card{padding:10px 6px!important}' +
          P + ' .quantity-bundle-title{font-size:12px!important;line-height:1.2}' +
          P + ' .quantity-bundle-save-pill{font-size:9.5px!important;padding:1px 5px!important}' +
          P + ' .quantity-bundle-badge{font-size:8.5px!important;padding:2px 6px!important}' +
        '}';
    },
  };

  /* An image at its scaled size. A freshly uploaded file can give a 502 on
     the scaled size while Shopify is still processing it, and some originals
     do not display at all (the scaled copy does): so retry the scaled size a
     few times first, and only then try the original. */
  function zetBeeld(img, url) {
    var klein = sizedImage(url);
    var poging = 0;
    img.onerror = klein === url ? null : function () {
      poging++;
      if (poging <= 3) {
        setTimeout(function () { img.src = klein + '&r=' + poging; }, 1500 * poging);
      } else {
        img.onerror = null;
        img.src = url;
      }
    };
    img.src = klein;
  }

  /* Shopify's CDN scales on ?width=; an uploaded original can be megabytes. */
  function sizedImage(url) {
    if (!/^https:\/\/[^/]*(cdn\.shopify\.com|shopifycdn\.)/i.test(url) || /[?&]width=/.test(url)) return url;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=300';
  }

  /* An amount from a price as the card shows it ("389,25 zł", "$1,565.00"). */
  function bedrag(tekst) {
    var m = String(tekst || '').match(/\d[\d.,\s  '’]*\d|\d/);
    if (!m) return NaN;
    var num = m[0].replace(/[\s  '’]/g, '');
    var laatste = Math.max(num.lastIndexOf(','), num.lastIndexOf('.'));
    if (laatste !== -1 && num.length - laatste - 1 === 2) {
      return parseFloat(num.slice(0, laatste).replace(/[.,]/g, '') + '.' + num.slice(laatste + 1));
    }
    return parseFloat(num.replace(/[.,]/g, ''));
  }

  /* `amount` written the way `voorbeeld` writes money: same symbol, separators
     and decimals. So it is always the visitor's own currency, as the theme shows it. */
  function zoalsPrijs(voorbeeld, amount) {
    var m = String(voorbeeld || '').match(/\d[\d.,\s  '’]*\d|\d/);
    if (!m || !isFinite(amount)) return null;
    var num = m[0];
    var laatste = Math.max(num.lastIndexOf(','), num.lastIndexOf('.'));
    var decimalen = laatste !== -1 && num.length - laatste - 1 === 2;
    var heelDeel = decimalen ? num.slice(0, laatste) : num;
    var duizend = (heelDeel.match(/[.,\s  '’]/) || [''])[0];
    var vast = amount.toFixed(decimalen ? 2 : 0).split('.');
    var uit = vast[0].replace(/\B(?=(\d{3})+(?!\d))/g, duizend) + (decimalen ? num.charAt(laatste) + vast[1] : '');
    return voorbeeld.slice(0, m.index) + uit + voorbeeld.slice(m.index + num.length);
  }

  /* Units on a card in B: paid (data-bogo-multiplier) plus B's free units. */
  function unitsB(card, b) {
    var total = parseInt(card.getAttribute('data-quantity'), 10) || 1;
    var paid = parseInt(card.getAttribute('data-bogo-multiplier'), 10);
    if (typeof b.free === 'number' && paid > 0 && total > paid) return paid + b.free;
    return total;
  }

  var TOKEN_RE = /\{(per_stuk|prijs|stuks|waarde)\}/g;

  /* Price of a gift (for {waarde}), from /products/<handle>.js: the visitor's
     own currency, as Shopify sells it to them. The higher of price and
     compare-at, since it is the value they get for free. */
  var giftWaarde = {};
  var giftWachters = {};
  function laadWaarde(handle, klaar) {
    if (typeof giftWaarde[handle] === 'number' || giftWaarde[handle] === false) return;
    (giftWachters[handle] = giftWachters[handle] || []).push(klaar);
    if (giftWaarde[handle] === null) return; // already loading
    giftWaarde[handle] = null;
    fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        var c = p ? Math.max(Number(p.price) || 0, Number(p.compare_at_price) || 0) : 0;
        giftWaarde[handle] = c > 0 ? c : false;
      })
      .catch(function () { giftWaarde[handle] = false; })
      .then(function () {
        var wachters = giftWachters[handle] || [];
        giftWachters[handle] = [];
        wachters.forEach(function (f) { try { f(); } catch (_e) { /* noop */ } });
      });
  }

  /* B's lines on a card: the list, or the single `note` of the first version. */
  function regelsVan(b) {
    if (b && Array.isArray(b.notes)) return b.notes.filter(function (r) { return r && r.text; });
    if (b && b.note && b.note.mode === 'custom' && b.note.text) return [b.note];
    return [];
  }

  /* Text with {per_stuk}, {prijs}, {stuks} filled in from the price on the
     card, and {waarde} with the value of the line's gift. Until that value is
     known (or when it cannot be), the "({waarde} value)" part is left out. */
  function vulIn(tekst, card, b, regel) {
    if (!TOKEN_RE.test(tekst)) return tekst;
    TOKEN_RE.lastIndex = 0;
    var prijsEl = card.querySelector('.quantity-current-price');
    var prijs = prijsEl ? prijsEl.textContent.trim() : '';
    var n = unitsB(card, b);
    var handle = regel && regel.handle;
    var cents = handle ? giftWaarde[handle] : false;
    if (handle && typeof cents !== 'number' && cents !== false) laadWaarde(handle, function () { prijsDelen(card, b); });
    var waarde = typeof cents === 'number' && prijs ? zoalsPrijs(prijs, cents / 100) : null;
    if (!waarde) tekst = tekst.replace(/\s*\([^)]*\{waarde\}[^)]*\)/g, '').replace(/\{waarde\}/g, '');
    return tekst.replace(TOKEN_RE, function (_, t) {
      if (t === 'stuks') return String(n);
      if (t === 'prijs') return prijs;
      if (t === 'waarde') return waarde || '';
      return zoalsPrijs(prijs, bedrag(prijs) / n) || '';
    });
  }

  function zetTekst(el, tekst) {
    if (el.textContent !== tekst) el.textContent = tekst;
  }

  /**
   * Everything on a B card that depends on the price. The theme's bundle code
   * rewrites prices whenever something changes (subscription toggle, variant),
   * so this runs again after every change inside the card.
   * With fewer free units: the compare-at price and the price per unit are
   * the theme's numbers for A's units, scaled to B's units; the Save pill
   * follows from the two amounts on the card.
   */
  function prijsDelen(card, b) {
    var aUnits = parseInt(card.getAttribute('data-quantity'), 10) || 1;
    var bUnits = unitsB(card, b);
    if (bUnits !== aUnits) {
      card.querySelectorAll('.quantity-original-price').forEach(function (el) {
        var tekst = el.textContent.trim();
        if (!tekst || tekst === el.getAttribute('data-hb-uit')) return;
        var nieuw = zoalsPrijs(tekst, bedrag(tekst) * bUnits / aUnits);
        if (!nieuw) return;
        el.setAttribute('data-hb-uit', nieuw);
        el.textContent = nieuw;
      });
      card.querySelectorAll('.quantity-per-item-price .price-amount, .quantity-bundle-per-item .price-amount, .quantity-bundle-grid-per-item .price-amount').forEach(function (el) {
        var tekst = el.textContent.trim();
        if (!tekst || tekst === el.getAttribute('data-hb-uit')) return;
        var nieuw = zoalsPrijs(tekst, bedrag(tekst) * aUnits / bUnits);
        if (!nieuw) return;
        el.setAttribute('data-hb-uit', nieuw);
        el.textContent = nieuw;
      });
    }
    zetSave(card);
    if (b.title) card.querySelectorAll('.quantity-bundle-title').forEach(function (el) { zetTekst(el, vulIn(b.title, card, b)); });
    if (b.sub) card.querySelectorAll('.quantity-bundle-description').forEach(function (el) { zetTekst(el, vulIn(b.sub, card, b)); });
    var regels = regelsVan(b);
    card.querySelectorAll('.hb-ab-note').forEach(function (el) {
      var r = regels[Number(el.getAttribute('data-i'))];
      if (!r) return;
      zetTekst(el.querySelector('.hb-ab-note__titel'), vulIn(r.text || '', card, b, r));
      var klein = el.querySelector('.hb-ab-note__sub');
      if (klein) zetTekst(klein, vulIn(r.sub || '', card, b, r));
    });
  }

  /* Whether the visitor is buying on subscription now: the same test as the
     add (a selling plan in the form, or the subscription toggle ticked). */
  function kaartAbo() {
    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    for (var i = 0; i < forms.length; i++) {
      if (!forms[i].querySelector('[name="id"]')) continue;
      var sp = forms[i].querySelector('[name="selling_plan"]');
      if (sp && sp.value) return true;
      break;
    }
    return !!tickedSubscription();
  }

  /**
   * Value (cents, visitor's currency) of the gifts this card unlocks in B, by
   * the same rules as the add: B's gifts and free units, subscription or not.
   * Unknown values are fetched; the card is redone when they arrive.
   */
  function cadeauWaarde(card) {
    var ab = pageAb;
    var cfg = ab && ab.cfg;
    if (!cfg) return 0;
    var total = parseInt(card.getAttribute('data-quantity'), 10) || 1;
    var bogo = parseInt(card.getAttribute('data-bogo-multiplier'), 10);
    var paid = bogo > 0 && total > bogo ? bogo : total;
    var qty = total;
    var bTier = ab.groups[String(total)];
    if (bTier && typeof bTier.free === 'number' && paid < total) qty = paid + bTier.free;
    if (card.getAttribute('data-bundle-variant')) { qty = topTier(cfg); paid = qty; }
    var handles = {};
    (cfg.tiers || []).forEach(function (t) {
      var lijsten = [t.gifts || []];
      if (t.experiment && t.experiment.b) lijsten.push(t.experiment.b.gifts || []);
      lijsten.forEach(function (l) { l.forEach(function (g) { if (g && g.id && g.handle) handles[String(g.id)] = g.handle; }); });
    });
    var som = 0;
    giftIdsFor(cfg, qty, kaartAbo(), ab, paid).forEach(function (id) {
      var h = handles[id];
      if (!h) return;
      var c = giftWaarde[h];
      if (typeof c === 'number') som += c;
      else if (c !== false) laadWaarde(h, function () { zetSave(card); });
    });
    return som;
  }

  /**
   * "Save X%" on a tested bundle in B: against the compare-at price plus the
   * value of the free gifts, so a bundle with gifts shows what it is really
   * worth. Cards B does not test keep the theme's own percentage. Written into
   * a copy of the theme's pill: some templates have a script that keeps the
   * original pill at the plain price difference, and the two would otherwise
   * keep overwriting each other.
   */
  function zetSave(card) {
    if (!pageAb || pageAb.variant !== 'B') return;
    var orig = card.querySelector('.quantity-bundle-save-pill:not(.hb-ab-pill)');
    if (!orig) return;
    var pil = card.querySelector('.hb-ab-pill');
    if (!pil) {
      pil = orig.cloneNode(true);
      pil.classList.add('hb-ab-pill');
      orig.parentNode.insertBefore(pil, orig.nextSibling);
      card.setAttribute('data-hb-ab-save', '');
    }
    var nu = bedrag((card.querySelector('.quantity-current-price') || {}).textContent);
    var was = bedrag((card.querySelector('.quantity-original-price') || {}).textContent);
    var basis = orig.textContent.trim() || 'Save 0%';
    if (!isFinite(nu) || !isFinite(was) || was <= 0) { zetTekst(pil, basis); return; }
    var totaal = was + cadeauWaarde(card) / 100;
    if (totaal <= nu) { zetTekst(pil, basis); return; }
    var pct = Math.round((totaal - nu) / totaal * 100) + '%';
    zetTekst(pil, /\d+\s*%/.test(basis) ? basis.replace(/\d+\s*%/, pct) : 'Save ' + pct);
  }

  function applyCard(card, b) {
    if (card.getAttribute('data-hb-ab') === 'B') return;
    card.setAttribute('data-hb-ab', 'B');
    if (b.image) {
      card.setAttribute('data-hb-ab-img', '');
      var sel = card.closest('.quantity-bundle-selector');
      if (sel) sel.setAttribute('data-hb-ab-img', '');
      card.querySelectorAll('.quantity-bundle-horizontal-image img, .quantity-bundle-grid-image img').forEach(function (img) {
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.setAttribute('loading', 'eager');
        zetBeeld(img, b.image);
      });
    }
    var titles = card.querySelectorAll('.quantity-bundle-title');
    if (b.sub && !card.querySelector('.quantity-bundle-description')) {
      titles.forEach(function (el) {
        var p = document.createElement('p');
        p.className = 'quantity-bundle-description';
        el.parentNode.insertBefore(p, el.nextSibling);
      });
    }
    // The lines, as a block under the card (shown only while it is selected):
    // lines without a gift first (what is in the bundle), then those with one.
    var regels = regelsVan(b);
    if (regels.length) {
      var details = document.createElement('div');
      details.className = 'hb-ab-details';
      var zonder = [], met = [];
      regels.forEach(function (r, i) { (r.handle ? met : zonder).push(i); });
      function regel(i) {
        var r = regels[i];
        var div = document.createElement('div');
        div.className = 'hb-ab-note';
        div.setAttribute('data-i', String(i));
        div.innerHTML = '<svg class="hb-ab-note__check" viewBox="0 0 20 20" width="16" height="16" aria-hidden="true"><path fill="currentColor" d="M7.6 14.2 3.4 10l1.4-1.4 2.8 2.8 7.6-7.6L16.6 5z"/></svg>';
        var tekst = document.createElement('div');
        var titel = document.createElement('div');
        titel.className = 'hb-ab-note__titel';
        tekst.appendChild(titel);
        if (r.sub) {
          var sub = document.createElement('div');
          sub.className = 'hb-ab-note__sub';
          tekst.appendChild(sub);
        }
        div.appendChild(tekst);
        details.appendChild(div);
      }
      zonder.forEach(regel);
      met.forEach(regel);
      card.appendChild(details);
    }
    prijsDelen(card, b);
    // Keep it right when the theme rewrites the prices (subscription toggle).
    if (window.MutationObserver) {
      var wacht = false;
      new MutationObserver(function () {
        if (wacht) return;
        wacht = true;
        setTimeout(function () { wacht = false; prijsDelen(card, b); }, 30);
      }).observe(card, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['data-selling-plan-id'] });
    }
  }

  /* Swap the template's own layout <style> (same section, comes after the
     bundles) for the other one. Nothing happens when it already is that layout. */
  function applyLayout(selector, layout) {
    var section = selector.closest('[id^="shopify-section-"]');
    if (!section || section.hasAttribute('data-hb-ab-layout')) return;
    section.setAttribute('data-hb-ab-layout', layout);
    var raw = '#' + section.id;
    var own = null;
    section.querySelectorAll('style').forEach(function (st) {
      var t = st.textContent || '';
      if (!own && t.indexOf(raw + ' .quantity-bundle-container') !== -1 && t.indexOf(raw + ' .quantity-bundle-card') !== -1) own = st;
    });
    var stacked = !!own && /quantity-bundle-container\s*\{[^}]*flex-direction:\s*column/.test(own.textContent);
    if ((layout === 'stack') === stacked) return;
    var css = document.createElement('style');
    css.setAttribute('data-hb-ab', layout);
    css.textContent = LAYOUT_CSS[layout]('#' + (window.CSS && CSS.escape ? CSS.escape(section.id) : section.id));
    if (own) {
      own.media = 'not all';
      own.parentNode.insertBefore(css, own.nextSibling);
    } else {
      section.appendChild(css);
    }
  }

  /* Which gifts the template's gifts box unlocks: the same rules as its own
     script (paid units of the chosen card, subscription for data-abo-nodig). */
  function boxBetaald(kaart) {
    if (!kaart) return 1;
    var qty = parseInt(kaart.getAttribute('data-quantity') || '1', 10) || 1;
    var bogo = parseFloat(kaart.getAttribute('data-bogo-multiplier') || '') || 0;
    var titel = (kaart.getAttribute('data-title') || '').toLowerCase();
    if (kaart.getAttribute('data-bundle-variant')) return 3;
    if (titel.indexOf('detox') !== -1 || titel.indexOf('buy 3') !== -1) return 3;
    if (bogo > 0) return Math.max(1, Math.round(qty / (1 + bogo) * bogo));
    return qty;
  }

  /**
   * B's own gifts box: the template's "Free gifts" block with the gifts chosen
   * in the app. Same classes and data-nodig / data-abo-nodig as the template's
   * items, so its own script keeps unlocking them when the bundle changes.
   */
  function bouwCadeaublok(rijen) {
    document.querySelectorAll('.gh-gifts').forEach(function (doos) {
      var rij = doos.querySelector('.gh-gifts__rij');
      if (!rij || doos.getAttribute('data-hb-ab') === 'B') return;
      doos.setAttribute('data-hb-ab', 'B');
      var slotje = rij.querySelector('.gh-gift__slotje');
      var slotHtml = slotje ? slotje.outerHTML : '';
      var voorbeeld = ((document.querySelector('.quantity-current-price') || {}).textContent || '').trim();
      rij.innerHTML = '';
      rijen.forEach(function (r) {
        var g = document.createElement('div');
        g.className = 'gh-gift';
        g.setAttribute('data-nodig', String(r.nodig));
        if (r.abo) g.setAttribute('data-abo-nodig', '');
        var thumb = document.createElement('span');
        thumb.className = 'gh-gift__thumb';
        if (r.image) {
          var img = document.createElement('img');
          zetBeeld(img, r.image);
          img.alt = '';
          img.loading = 'lazy';
          thumb.appendChild(img);
        } else {
          var em = document.createElement('span');
          em.className = 'gh-gift__emoji';
          em.textContent = '🎁';
          thumb.appendChild(em);
        }
        var tekst = document.createElement('span');
        tekst.className = 'gh-gift__tekst';
        var naam = document.createElement('span');
        naam.className = 'gh-gift__naam';
        naam.textContent = r.title;
        var waarde = document.createElement('span');
        waarde.className = 'gh-gift__waarde';
        waarde.innerHTML = slotHtml;
        var was = document.createElement('s');
        var free = document.createElement('b');
        free.textContent = 'FREE';
        waarde.appendChild(was);
        waarde.appendChild(free);
        tekst.appendChild(naam);
        tekst.appendChild(waarde);
        g.appendChild(thumb);
        g.appendChild(tekst);
        rij.appendChild(g);
        function zetWaarde() {
          var c = r.handle ? giftWaarde[r.handle] : false;
          was.textContent = typeof c === 'number' && voorbeeld ? (zoalsPrijs(voorbeeld, c / 100) || '') : '';
        }
        if (r.handle) laadWaarde(r.handle, zetWaarde);
        zetWaarde();
      });
      // "x of 3 unlocked": only the number after the counter changes, the
      // counter element itself belongs to the template's script.
      var stand = doos.querySelector('.gh-gifts__stand');
      if (stand) {
        stand.childNodes.forEach(function (n) {
          if (n.nodeType === 3 && /\d+/.test(n.textContent)) n.textContent = n.textContent.replace(/\d+/, String(rijen.length));
        });
      }
      var kaart = document.querySelector('.quantity-bundle-card[data-selected="true"]');
      var cb = document.querySelector('.subscribe-save-wrapper input[type="checkbox"], .subscribe-save-wrapper-dynamic input[type="checkbox"]');
      var abo = !!(cb && cb.checked);
      var n = boxBetaald(kaart);
      var vrij = 0;
      rij.querySelectorAll('.gh-gift').forEach(function (g) {
        var mag = n >= parseInt(g.getAttribute('data-nodig'), 10) && (!g.hasAttribute('data-abo-nodig') || abo);
        g.classList.toggle('is-vrij', mag);
        if (mag) vrij++;
      });
      var teller = doos.querySelector('[data-teller]');
      if (teller) teller.textContent = String(vrij);
    });
  }

  function abApply() {
    if (pageAb === undefined) return; // config not known yet; runs again when it is
    docEl.classList.remove('hb-ab-wait');
    var ab = pageAb;
    if (!ab || ab.variant !== 'B') {
      docEl.classList.remove('hb-ab-relayout', 'hb-ab-hidebox', 'hb-ab-noonetime', 'hb-ab-nosave', 'hb-ab-ownbox');
      return;
    }
    var b = ab.page || {};
    document.querySelectorAll('.quantity-bundle-selector').forEach(function (sel) { sel.setAttribute('data-hb-ab', 'B'); });
    if (b.hideGiftsBox) docEl.classList.add('hb-ab-hidebox');
    if (b.hideOneTime) docEl.classList.add('hb-ab-noonetime');
    if (b.hideSave) docEl.classList.add('hb-ab-nosave');
    if (Array.isArray(b.giftsBox) && b.giftsBox.length && !b.hideGiftsBox) {
      docEl.classList.add('hb-ab-ownbox');
      bouwCadeaublok(b.giftsBox);
    }
    Object.keys(ab.groups).forEach(function (group) {
      document.querySelectorAll('.quantity-bundle-card[data-quantity="' + group + '"]').forEach(function (card) {
        applyCard(card, ab.groups[group]);
      });
    });
    var relayout = b.layout === 'stack' || b.layout === 'cols';
    if (relayout && document.readyState !== 'loading') {
      // The cards have transition: all; without this they would visibly morph
      // into the other layout. Layout is computed once with transitions off.
      docEl.classList.add('hb-ab-notrans');
      document.querySelectorAll('.quantity-bundle-selector').forEach(function (sel) { applyLayout(sel, b.layout); });
      void docEl.offsetHeight;
      docEl.classList.remove('hb-ab-relayout');
      requestAnimationFrame(function () { docEl.classList.remove('hb-ab-notrans'); });
    } else if (relayout) {
      docEl.classList.add('hb-ab-relayout');
    }
  }

  if (SETTINGS.productId) {
    // Known now when the config is cached for this session: B is in place
    // before anything paints, and A visitors never wait.
    var cachedNow = readCache();
    if (cachedNow) pageAb = abFor(productConfig(cachedNow, SETTINGS.productId));
    else docEl.classList.add('hb-ab-wait');
    abApply();
    pageAbReady().then(abApply);
    setTimeout(function () { docEl.classList.remove('hb-ab-wait'); }, AB_GATE_MS);
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', abApply);
    // Never keep the bundles hidden, whatever goes wrong above.
    window.addEventListener('load', function () { docEl.classList.remove('hb-ab-wait', 'hb-ab-relayout', 'hb-ab-ownbox'); });
  }

  // Start loading the config right away so a click rarely waits (on product
  // pages already started above).
  if (document.readyState !== 'loading') loadConfig();
  else document.addEventListener('DOMContentLoaded', loadConfig);

  window.HerbiesCart = {
    add: add,
    loadConfig: loadConfig,
    eligibleGiftIds: eligibleGiftIds,
    /* For the bundle block and the analytics snippet. */
    abApply: abApply,
    ab: function () {
      return pageAbReady().then(function (ab) { return ab ? { id: ab.id, variant: ab.variant } : null; });
    },
  };

  /* Backwards compatible entry point for the PDP buttons (onclick="skipToCheckout(event)"). */
  window.skipToCheckout = function (e) {
    if (e && e.preventDefault) e.preventDefault();
    var btn = (e && e.currentTarget) || null;
    var form = btn && btn.closest ? btn.closest('form') : null;
    return add({ form: form, button: btn, mode: 'checkout' });
  };
})();
